/**
 * Utility functions for consistent error handling across the application
 * Prevents [object Object] errors in console logs and user-facing messages
 */

/**
 * Extracts a meaningful error message from any error type
 * @param error - The error object, string, or unknown type
 * @param fallbackMessage - Default message if no error message can be extracted
 * @returns A readable error message string
 */
export function getErrorMessage(
	error: unknown,
	fallbackMessage: string = "An unexpected error occurred",
): string {
	if (!error) {
		return fallbackMessage;
	}

	// If it's already a string, return it
	if (typeof error === "string") {
		return error;
	}

	// If it's an Error instance, get the message
	if (error instanceof Error) {
		return error.message || fallbackMessage;
	}

	// If it's an object with a message property
	if (typeof error === "object" && error !== null && "message" in error) {
		const message = (error as any).message;
		if (typeof message === "string") {
			return message;
		}
	}

	// If it's a Supabase error object
	if (typeof error === "object" && error !== null && "error" in error) {
		const supabaseError = (error as any).error;
		if (typeof supabaseError === "string") {
			return supabaseError;
		}
		if (
			supabaseError &&
			typeof supabaseError === "object" &&
			"message" in supabaseError
		) {
			return supabaseError.message || fallbackMessage;
		}
	}

	// Last resort: try JSON.stringify but catch any errors
	try {
		return JSON.stringify(error);
	} catch {
		return fallbackMessage;
	}
}

/**
 * Logs an error with proper message extraction
 * @param message - The log message prefix
 * @param error - The error to log
 * @param level - Log level (error, warn, info)
 */
export function logError(
	message: string,
	error: unknown,
	level: "error" | "warn" | "info" = "error",
): void {
	const errorMessage = getErrorMessage(error);

	// Log both the extracted message and the original error object for debugging
	console[level](`${message}: ${errorMessage}`, error);
}

/**
 * Creates a user-friendly error message for display in UI
 * @param error - The error object
 * @param context - Context about what was being done when the error occurred
 * @returns A user-friendly error message
 */
export function getUserFriendlyErrorMessage(
	error: unknown,
	context: string,
): string {
	const errorMessage = getErrorMessage(error);

	// Common error patterns and their user-friendly messages
	if (errorMessage.includes("network") || errorMessage.includes("fetch")) {
		return `Network error while ${context}. Please check your connection and try again.`;
	}

	if (
		errorMessage.includes("authorization") ||
		errorMessage.includes("unauthorized")
	) {
		return `Authentication error while ${context}. Please sign in again.`;
	}

	if (errorMessage.includes("not found") || errorMessage.includes("404")) {
		return `The requested data was not found while ${context}.`;
	}

	if (errorMessage.includes("timeout")) {
		return `Request timed out while ${context}. Please try again.`;
	}

	// Return a generic but informative message
	return `An error occurred while ${context}: ${errorMessage}`;
}

/**
 * A more robust console.error replacement that handles any error type
 */
export const safeConsoleError = (message: string, error: unknown): void => {
	logError(message, error, "error");
};

/**
 * A more robust console.warn replacement that handles any error type
 */
export const safeConsoleWarn = (message: string, error: unknown): void => {
	logError(message, error, "warn");
};
